<script lang="ts" generics="ROW">
    import type {Cell} from '@tanstack/svelte-table';
    import {getGridContext} from 'shared/ui-widgets/grid/grid.context';
    import {isLastColumnInMainColumnGroup} from 'shared/ui-widgets/grid/utils';
    import {CellCoordinates} from 'shared/ui-widgets/grid/types';
    import {setGridCellContext} from './grid-cell.context';
    import {exists} from 'shared/utils/custom-utils';
    import {distinctStore} from 'shared/utils/store-utils';
    import {
        isBottomBorder,
        isCellInEditMode,
        isCursor,
        isHighlightedDependency,
        isHover,
        isLeftBorder,
        isRightBorder,
        isSelected,
        isTopBorder,
        isVirtualCursor
    } from '../state/state-query';

    export let cell: Cell<ROW, unknown>;
    export let rowIndex: number;
    export let columnIndex: number;

    let gridCellRef: HTMLElement;
    let thisCellCoordinates = new CellCoordinates({rowIndex, columnIndex});

    const {
        settings: {columnPinningEnabled, mainGroupsDivided, columnResizingEnabled, condensed},
        stateManager: {state$}
    } = getGridContext();

    const coordinates$ = distinctStore(thisCellCoordinates);
    const {contentDependentSate$} = setGridCellContext(coordinates$);

    $: isColumnGroupBorder = mainGroupsDivided && isLastColumnInMainColumnGroup(cell.column);
    $: pinnedLeft = columnPinningEnabled && cell.column.getIsPinned() === 'left';
    $: leftOffset = (columnPinningEnabled || columnResizingEnabled) ? `${cell.column.getStart('left')}px` : '0px';
    $: thisCellCoordinates = new CellCoordinates({rowIndex, columnIndex});
    $: coordinates$.set(thisCellCoordinates);
    $: hover = isHover($state$, thisCellCoordinates);
    $: cursor = isCursor($state$, thisCellCoordinates);
    $: selected = isSelected($state$, thisCellCoordinates);
    $: virtualCursor = isVirtualCursor($state$, thisCellCoordinates);
    $: editModeActive = isCellInEditMode($state$, thisCellCoordinates);
    $: topBorder = isTopBorder($state$, thisCellCoordinates);
    $: rightBorder = isRightBorder($state$, thisCellCoordinates);
    $: bottomBorder = isBottomBorder($state$, thisCellCoordinates);
    $: leftBorder = isLeftBorder($state$, thisCellCoordinates);
    $: border = topBorder || rightBorder || bottomBorder || leftBorder;
    $: invalid = $contentDependentSate$.invalid;
    $: disabled = $contentDependentSate$.disabled;
    $: highlightedDependency = isHighlightedDependency($state$, thisCellCoordinates) && !cursor;
    $: {
        if (cursor && !editModeActive && exists(gridCellRef)) {
            gridCellRef.focus();
        }
    }

    function isEven(val: number) {
        return val % 2 === 0;
    }
</script>

<div role="gridcell"
     tabindex="-1"
     bind:this={gridCellRef}
     class="kp-generic-grid-cell"
     data-row-index="{rowIndex}"
     data-column-index="{columnIndex}"
     title="[{rowIndex},{columnIndex}]"
     class:column-group-border={isColumnGroupBorder}
     class:pinned-left={pinnedLeft}
     class:active={cursor || virtualCursor}
     class:even-depth={isEven(cell.row.depth ?? 0)}
     class:odd-depth={!isEven(cell.row.depth ?? 0)}
     class:selected
     class:hover
     class:invalid
     class:disabled
     class:highlighted-dependency={highlightedDependency}
     style:width="{cell.column.getSize()}px"
     style:--left-offset="{leftOffset}">

    <div class="cell-content" class:condensed>
        <slot/>
    </div>

    {#if cursor || border || highlightedDependency}
        {#if cursor || topBorder || highlightedDependency}
            <div class="cell-outline top"
                 class:wide={cursor}
                 class:highlighted-dependency={highlightedDependency}></div>
        {/if}

        {#if cursor || rightBorder || highlightedDependency}
            <div class="cell-outline right"
                 class:wide={cursor}
                 class:highlighted-dependency={highlightedDependency}></div>
        {/if}

        {#if cursor || bottomBorder || highlightedDependency}
            <div class="cell-outline bottom"
                 class:wide={cursor}
                 class:highlighted-dependency={highlightedDependency}></div>
        {/if}

        {#if cursor || leftBorder || highlightedDependency}
            <div class="cell-outline left"
                 class:wide={cursor}
                 class:highlighted-dependency={highlightedDependency}></div>
        {/if}
    {/if}

    {#if virtualCursor}
        <div class="virtual-cursor"></div>
    {/if}
</div>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";
    @import (reference) "styles/portaro.themes.less";

    @grey: @table-bg-accent;
    @default-border-width: 1px;

    .kp-generic-grid-cell {
        position: relative;
        flex: none; // row is flexbox, cell is fixed size
        z-index: 3;
        border-bottom: @default-border-width solid @themed-border-muted;
        border-right: var(--col-border-width, @default-border-width) solid @themed-border-muted;

        /* &.active {
            z-index: 5;
        } */

        &.selected {
            background-color: @themed-body-bg-orange-highlighted !important;
        }

        &.column-group-border {
            --col-border-width: 2px; // TODO: check if it works ok
        }

        &.pinned-left {
            position: sticky;
            left: var(--left-offset, 0px);
            z-index: var(--z-index, 5);
        }

        &.hover {
            background-color: @table-bg-hover;
        }

        &:not(.hover).even-depth {
            background-color: #ffffff;
        }

        &:not(.hover).odd-depth {
            background-color: @grey;
        }

        &.invalid {
            --hatch-color: #efc1c1;
            .hatch-pattern();
        }

        &.disabled {
            --hatch-color: #f5f5f5;
            .hatch-pattern();
            cursor: not-allowed;
        }

        &.highlighted-dependency {
            background-color: #ECDCFF !important;
        }

        .cell-content {
            height: 100%;
            width: 100%;
            padding: @table-condensed-cell-padding;
            line-height: @line-height-base;
            white-space: nowrap;
            align-content: center;
            overflow: hidden;
            background: transparent;

            &.condensed {
                padding: 3px;
                font-size: 12px;
            }
        }

        .cell-outline {
            position: absolute;
            z-index: 0;
            background-color: var(--brand-orange-new);

            &.highlighted-dependency {
                background-color: #9747FF;
            }

            &.wide {
                --outline-width: 3px;
                background-color: var(--brand-orange-new);
            }

            &.top {
                top: -1px;
                left: -1px;
                height: var(--outline-width, 2px);
                width: calc(100% + 2px);
            }

            &.right {
                top: -1px;
                right: -1px;
                height: calc(100% + 2px);
                width: var(--outline-width, 2px);
            }

            &.bottom {
                bottom: -1px;
                left: -1px;
                height: var(--outline-width, 2px);
                width: calc(100% + 2px);
            }

            &.left {
                top: -1px;
                left: -1px;
                height: calc(100% + 2px);
                width: var(--outline-width, 2px);
            }
        }

        .virtual-cursor {
            @cursor-size: 8px;

            position: absolute;
            z-index: 10;
            height: @cursor-size;
            width: @cursor-size;
            bottom: 0;
            right: 0;
            background-color: var(--brand-orange-new);

            opacity: 0.5;
        }
    }

    .hatch-pattern() {
        background-image: linear-gradient(-45deg,
        var(--hatch-color, transparent) 25%,
        transparent 25%,
        transparent 50%,
        var(--hatch-color, transparent) 50%,
        var(--hatch-color, transparent) 75%,
        transparent 75%,
        transparent 100%);
        background-size: 15px 15px;
    }
</style>
